import { useState, useEffect } from 'react';
import { useProductSettings } from '@workspace/utils-react';
import type { Settings } from '../../AppContainer';
import { getPrompts } from '../../api/prompts';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Spinner } from '../../components/ui/spinner';
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/alert';
import { AlertCircle } from 'lucide-react';

interface PromptEntry {
  name: string;
  content: string;
}

export function PromptsContent() {
  const { apiUrl } = useProductSettings<Settings>();
  const [prompts, setPrompts] = useState<PromptEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchPrompts() {
      try {
        const data = await getPrompts(apiUrl);
        setPrompts(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load prompts');
      } finally {
        setLoading(false);
      }
    }
    fetchPrompts();
  }, [apiUrl]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner className="h-8 w-8" />
        <span className="ml-3 text-muted-foreground">Loading prompts...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-5 w-5" />
        <div>
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </div>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Read-only view of the prompts used for batch AI assessments and project evaluations.
      </p>

      {prompts.map((prompt) => (
        <Card key={prompt.name}>
          <CardHeader>
            <CardTitle className="text-base">{prompt.name}</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="whitespace-pre-wrap font-mono text-xs bg-muted p-4 rounded-md border max-h-[500px] overflow-y-auto">
              {prompt.content}
            </pre>
          </CardContent>
        </Card>
      ))}

      {prompts.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-8">
          No prompts configured.
        </p>
      )}
    </div>
  );
}
